import {
  PDF_FONT_SIZE,
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportTableCell,
  type ReportTableHeaderCell,
} from '@/components';
import { LEDGER_BG_COLOR, sortBelanjaProgramData } from '@/features/mold/utils';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { FALLBACK_TEXT, formatFraction, formatValueByUnit } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

type Props = {
  project: z.infer<typeof ProjectModel>;
};

const BelanjaProgramCellRatio = ['100px', '100px', '100px', '50px'];

export const BiayaProgram: FC<Props> = ({ project }) => {
  const { t } = useTranslation();
  const fiscalYear = project.fiscalYear;

  const HEADER_CELLS: ReportTableCell[] = [
    {
      type: 'text',
      textStyle: { fontSize: PDF_FONT_SIZE.XL },
      value: t('finalized-amount'),
      align: 'center',
      textWrapperStyle: { height: '100%' },
    },
    {
      type: 'text',
      textStyle: { fontSize: PDF_FONT_SIZE.XL },
      value: t('next-request-amount'),
      align: 'center',
      textWrapperStyle: { height: '100%' },
    },
    {
      type: 'text',
      textStyle: { fontSize: PDF_FONT_SIZE.XL },
      value: t('execution-amount'),
      align: 'center',
      textWrapperStyle: { height: '100%' },
    },
    {
      type: 'text',
      textStyle: { fontSize: PDF_FONT_SIZE.XL },
      value: t('percentage-unit'),
      align: 'center',
      textWrapperStyle: { height: '100%' },
    },
  ];

  const belanjaHeader: ReportTableHeaderCell[] = [
    {
      type: 'text',
      value: t('uraian-belanja-program'),
      textStyle: { textAlign: 'center' },
      ratio: 10,
    },
    {
      type: 'multiple',
      wrapperStyle: { padding: 0 },
      cell: {
        type: 'flex',
        vertical: true,
        flexCells: [
          {
            type: 'text',
            value: fiscalYear,
            align: 'center',
            textWrapperStyle: { height: '100%' },
            textStyle: { fontSize: PDF_FONT_SIZE.XL },
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              cellRatios: BelanjaProgramCellRatio,
              flexCells: HEADER_CELLS,
            },
          },
        ],
      },
      ratio: 'auto',
    },
    {
      type: 'multiple',
      wrapperStyle: { padding: 0 },
      cell: {
        type: 'flex',
        vertical: true,
        flexCells: [
          {
            type: 'text',
            value: fiscalYear - 1,
            textWrapperStyle: { height: '100%' },
            align: 'center',
            textStyle: { fontSize: PDF_FONT_SIZE.XL },
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              cellRatios: BelanjaProgramCellRatio,
              flexCells: HEADER_CELLS,
            },
          },
        ],
      },
      ratio: 'auto',
    },
  ];

  return (
    <ReportSection
      title={t('title-belanja-program')}
      items={[
        {
          indexText: '15.',
          subtitle: 'Belanja\nProgram',
          item: (
            <ReportSectionItemContent
              isTable
              isLast
              value={
                <ReportSectionItemTable
                  header={belanjaHeader}
                  data={
                    sortBelanjaProgramData(
                      project.indonesiaData?.belanjaProgram,
                    )?.map<{
                      row: ReportTableCell[];
                    }>((v) => {
                      return {
                        row: [
                          {
                            type: 'text',
                            value: `${v.parentId ? `- ${v.childIndex}.` : ''}${v.name}`,
                          },
                          // n年度
                          {
                            type: 'multiple',
                            wrapperStyle: { padding: 0 },
                            cell: {
                              type: 'flex',
                              cellRatios: BelanjaProgramCellRatio,
                              flexCells: [
                                {
                                  type: 'text',
                                  value: formatValueByUnit(
                                    v.currentYearAnggaranAPBD,
                                  ),
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                                {
                                  type: 'text',
                                  value: formatValueByUnit(
                                    v.currentYearAnggaranLainnya,
                                  ),
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                                {
                                  type: 'text',
                                  value: formatValueByUnit(
                                    v.currentYearRealisasi,
                                  ),
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                                {
                                  type: 'text',
                                  value: v.currentYearRate
                                    ? formatFraction(v.currentYearRate, 100)
                                    : null,
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                              ],
                            },
                          },
                          // n-1年度
                          {
                            type: 'multiple',
                            wrapperStyle: { padding: 0 },
                            cell: {
                              type: 'flex',
                              cellRatios: BelanjaProgramCellRatio,
                              flexCells: [
                                {
                                  type: 'text',
                                  value: formatValueByUnit(
                                    v.previousYearAnggaranAPBD,
                                  ),
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                                {
                                  type: 'text',
                                  value: formatValueByUnit(
                                    v.previousYearAnggaranLainnya,
                                  ),
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                                {
                                  type: 'text',
                                  value: formatValueByUnit(
                                    v.previousYearRealisasi,
                                  ),
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                                {
                                  type: 'text',
                                  value: v.previousYearRate
                                    ? formatFraction(v.previousYearRate, 100)
                                    : null,
                                  align: 'right',
                                  textStyle: { fontSize: PDF_FONT_SIZE.XL },
                                },
                              ],
                            },
                          },
                        ],
                      };
                    }) ?? [{ row: [{ type: 'text', value: FALLBACK_TEXT }] }]
                  }
                />
              }
            />
          ),
        },
        {
          indexText: '16.',
          subtitle: t('increase-decrease-main-reason'),
          item: (
            <ReportSectionItemContent
              value={project?.increaseDecreaseMainReason}
            />
          ),
        },
        {
          indexText: '17.',
          subtitle: 'Biaya Tenaga (realisasi)',
          item: (
            <ReportSectionItemTable
              header={[
                {
                  type: 'text',
                  value: 'Uraian',
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear }),
                  textStyle: { textAlign: 'center' },
                  ratio: 40,
                  colSpan: 4,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear - 1 }),
                  textStyle: { textAlign: 'center' },
                  ratio: 40,
                  colSpan: 4,
                },
              ]}
              data={
                project.indonesiaData?.biayaTenaga?.map<{
                  row: ReportTableCell[];
                }>((v) => {
                  return {
                    row: [
                      { type: 'text', value: v.name },
                      // 今年度
                      { type: 'text', value: v.currentYearOrg },
                      {
                        type: 'text',
                        value: t('org'),
                        align: 'center',
                        wrapperStyle: { background: LEDGER_BG_COLOR },
                      },
                      { type: 'text', value: v.currentYearRp },
                      {
                        type: 'text',
                        value: t('rp'),
                        align: 'center',
                        wrapperStyle: { background: LEDGER_BG_COLOR },
                      },
                      // 前年度
                      { type: 'text', value: v.previousYearOrg },
                      {
                        type: 'text',
                        value: t('org'),
                        align: 'center',
                        wrapperStyle: { background: LEDGER_BG_COLOR },
                      },
                      { type: 'text', value: v.previousYearRp },
                      {
                        type: 'text',
                        value: t('rp'),
                        align: 'center',
                        wrapperStyle: { background: LEDGER_BG_COLOR },
                      },
                    ],
                  };
                }) ?? [{ row: [{ type: 'text', value: FALLBACK_TEXT }] }]
              }
            />
          ),
        },
        {
          indexText: '18.',
          subtitle: 'Biaya Tenaga (realisasi)',
          item: (
            <ReportSectionItemTable
              header={[
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear }),
                  textStyle: { textAlign: 'center' },
                  ratio: 1,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear - 1 }),
                  textStyle: { textAlign: 'center' },
                  ratio: 1,
                },
              ]}
              data={[
                {
                  row: [
                    {
                      type: 'text',
                      value: formatValueByUnit(
                        project.indonesiaData?.totalCurrentYearBiayaTenaga,
                      ),
                      align: 'right',
                    },
                    {
                      type: 'text',
                      value: formatValueByUnit(
                        project.indonesiaData?.totalPreviousYearBiayaTenaga,
                      ),
                      align: 'right',
                    },
                  ],
                },
              ]}
            />
          ),
        },
        {
          indexText: '19.',
          subtitle: t('revenue-source'),
          item: <ReportSectionItemContent value={project?.privateReason} />,
        },
        {
          indexText: '20.',
          subtitle: t('other-special-notes'),
          item: <ReportSectionItemContent value={project?.otherSpecialNotes} />,
        },
      ]}
    />
  );
};
